"use client";

import type { FC } from "react";
import type { DemoConfigDeveloper } from "@/app/_blocks/BlockOmniboxDeveloper";
import type { CodeSnippet } from "./OmniboxDeveloper";

import { LanguageSelect } from "./LanguageSelect";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import clsx from "clsx";
import { useState, useEffect } from "react";

import { DemoContentDeveloper } from "@ui/components/demos/VoiceGeneratorDemos/DemoContentDeveloper";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";
import { CopyButton } from "@ui/components/core/Code/CopyButton";
import type { Speech } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Speech/Speech";
import { CodeFromTokens } from "@ui/components/core/Code/CodeFromTokens";
import type { CodeToken } from "@ui/lib/syntaxHighlighting";

type Mode = "demo" | "code";

interface DemoCardDeveloperProps {
  animationKey?: number | string;
  className?: string;
  demo: DemoConfigDeveloper;
  codeSnippets?: CodeSnippet[] | null;
  mode: Mode;
  speechProps: React.ComponentPropsWithoutRef<typeof Speech>;
}

const CodeView: FC<{ tokens: CodeToken[][] }> = ({ tokens }) => {
  return (
    <CodeFromTokens
      raw
      tokens={tokens}
      className='[&_code]:tw-font-mono [&_code]:tw-block tw-text-2xs tw-whitespace-pre-wrap tw-break-words tw-leading-relaxed'
    />
  );
};

export const DemoCardDeveloper: FC<DemoCardDeveloperProps> = ({
  animationKey,
  className,
  demo,
  codeSnippets,
  mode,
  speechProps,
}) => {
  const [activeSnippetIndex, setActiveSnippetIndex] = useState(0);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const hasCodeSnippets = codeSnippets && codeSnippets.length > 0;

  // Restore last selected language from localStorage
  useEffect(() => {
    if (!hasCodeSnippets) return;
    const stored = safeLocalStorage.getItem(LocalStorageKeys.DEVELOPER_OMNIBOX_LANGUAGE);
    if (stored) {
      // Find index by language name
      const idx = codeSnippets.findIndex(s => s.language === stored);
      if (idx !== -1) setActiveSnippetIndex(idx);
    }
  }, [hasCodeSnippets, codeSnippets]);

  // Save selected language to localStorage
  const handleTabChange = (index: number) => {
    setActiveSnippetIndex(index);
    if (hasCodeSnippets && codeSnippets[index]) {
      safeLocalStorage.setItem(LocalStorageKeys.DEVELOPER_OMNIBOX_LANGUAGE, codeSnippets[index].language);
    }
  };

  // Ensure active index is valid
  const effectiveIndex = hasCodeSnippets && activeSnippetIndex < codeSnippets.length ? activeSnippetIndex : 0;

  const activeSnippet = hasCodeSnippets ? codeSnippets[effectiveIndex] : null;

  const isCodeMode = mode === "code" && hasCodeSnippets;

  return (
    <div className={clsx("tw-w-full", className)}>
      <AnimatePresence mode='wait'>
        {isCodeMode && activeSnippet ? (
          <m.div
            key='code'
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className='tw-w-full'
          >
            <div className='tw-group/code tw-relative tw-rounded-3xl tw-flex tw-flex-col tw-h-[18rem] sm:tw-h-[22rem] tw-bg-white tw-shadow-sm tw-ring-1 tw-ring-gray-200'>
              {/* Floating controls - top right, show on hover or when select is open */}
              <div
                className={clsx(
                  "tw-absolute tw-top-2 tw-right-2 tw-z-20 tw-flex tw-flex-row tw-items-center tw-gap-2 tw-transition-opacity tw-duration-200",
                  isSelectOpen ? "tw-opacity-100" : "tw-opacity-0 group-hover/code:tw-opacity-100",
                )}
              >
                {codeSnippets.length > 1 && (
                  <LanguageSelect
                    activeIndex={effectiveIndex}
                    onSelect={handleTabChange}
                    snippets={codeSnippets}
                    onOpenChange={setIsSelectOpen}
                  />
                )}
                <CopyButton text={activeSnippet.snippet} />
              </div>
              <div className='tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-p-6'>
                <AnimatePresence mode='wait'>
                  <m.div
                    key={effectiveIndex}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.1 } }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                  >
                    <CodeView tokens={activeSnippet.tokens} />
                  </m.div>
                </AnimatePresence>
              </div>
            </div>
          </m.div>
        ) : (
          <m.div
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            initial={{ opacity: 0 }}
            key={`demo-${animationKey}`}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className='tw-w-full tw-relative tw-h-[18rem] sm:tw-h-[22rem] tw-flex tw-items-center tw-justify-center'
          >
            <div className='tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center'>
              <div className='tw-w-full'>
                <DemoContentDeveloper demo={demo} speechProps={speechProps} />
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DemoCardDeveloper;
